import { useAuth0 } from "@auth0/auth0-react";
import { UpChunk } from "@mux/upchunk";
import { useLoggedInAs } from "./useLoggedInAs";

const API_BASE = import.meta.env.VITE_API_SERVER_URL;

/** Soft app cap for gallery/org Mux uploads (chunked). Not a Mux platform limit. */
export const MAX_MUX_VIDEO_FILE_SIZE = 500_000_000; // 500MB

export type MuxUploadResult = {
  assetId: string;
  playbackId: string;
  /** `ready` when Mux finished processing; otherwise still preparing. */
  status: "preparing" | "ready";
  duration?: number;
  width?: number;
  height?: number;
};

export type MuxUploadOptions = {
  maxFileSize?: number;
  /**
   * `ready` (default): wait until Mux processing finishes (gallery uploads).
   * `asset_created`: return as soon as Mux has an assetId so the UI can show
   * a processing thumbnail without blocking the upload modal.
   */
  waitUntil?: "ready" | "asset_created";
};

/**
 * Router-safe wrapper around useLoggedInAs (same pattern as usePostHog's
 * useSaveLoggedInAs). useLoggedInAs calls react-router's useLocation(),
 * which THROWS when rendered outside a <Router> — e.g. in the embedded
 * event-submit widget, where UploadMediaModal mounts this hook on the
 * Photos step. The try/catch is stable per environment (always throws or
 * never throws for a given mount), so hook order is preserved.
 */
const useSaveLoggedInAs = () => {
  try {
    return useLoggedInAs();
  } catch {
    return null;
  }
};

/**
 * Upload a video to Mux via resumable chunked direct upload (UpChunk).
 *
 * Flow:
 *   1. Call our backend to create a Mux direct upload URL
 *   2. Upload the file to Mux in chunks via UpChunk
 *   3. Poll our backend for assetId + playbackId (optionally until ready)
 */
export const useMuxUpload = () => {
  const { getAccessTokenSilently } = useAuth0();
  const loggedInAs = useSaveLoggedInAs();

  const getToken = () =>
    getAccessTokenSilently({
      authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
    });

  const uploadVideo = async (
    file: File,
    onProgress: (percent: number) => void,
    options?: MuxUploadOptions,
  ): Promise<MuxUploadResult> => {
    const maxFileSize = options?.maxFileSize ?? MAX_MUX_VIDEO_FILE_SIZE;
    const waitUntil = options?.waitUntil ?? "ready";
    if (file.size > maxFileSize) {
      throw new Error(
        `Video is too large. Maximum size is ${Math.round(maxFileSize / 1_000_000)}MB.`,
      );
    }

    const token = await getToken();

    // Step 1: Create the Mux direct upload URL via our backend
    const createRes = await fetch(`${API_BASE}/mux/create-upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId: loggedInAs?.entityId,
      }),
    });

    if (!createRes.ok) {
      throw new Error("Failed to create upload URL");
    }

    const { uploadId, uploadUrl } = await createRes.json();

    // Step 2: Chunked resumable upload to Mux (UpChunk)
    await new Promise<void>((resolve, reject) => {
      const upload = UpChunk.createUpload({
        endpoint: uploadUrl,
        file,
        // 30MB chunks (must be a multiple of 256KB)
        chunkSize: 30720,
      });

      upload.on("error", event => {
        const message =
          typeof event.detail === "string"
            ? event.detail
            : ((event.detail as { message?: string } | undefined)?.message ??
              "Network error during chunked Mux upload");
        reject(new Error(message));
      });

      upload.on("progress", event => {
        // UpChunk progress detail is 0–100; reserve the last 20% for Mux processing
        const pct = Number(event.detail) || 0;
        onProgress(Math.min(80, Math.floor(pct * 0.8)));
      });

      upload.on("success", () => {
        onProgress(80);
        resolve();
      });
    });

    // Step 3: Poll for asset / playback
    onProgress(85);
    const result = await pollForMuxAsset(
      token,
      uploadId,
      onProgress,
      waitUntil,
    );
    onProgress(100);

    return result;
  };

  return { uploadVideo };
};

async function pollForMuxAsset(
  token: string,
  uploadId: string,
  onProgress: (percent: number) => void,
  waitUntil: "ready" | "asset_created",
): Promise<MuxUploadResult> {
  const maxAttempts = waitUntil === "asset_created" ? 30 : 60;
  const intervalMs = 2000;

  for (let i = 0; i < maxAttempts; i++) {
    await new Promise(r => setTimeout(r, intervalMs));
    onProgress(85 + Math.min(14, Math.floor((i / maxAttempts) * 14)));

    const res = await fetch(`${API_BASE}/mux/upload-status/${uploadId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) continue;

    const data = await res.json();

    if (data.status === "errored") {
      throw new Error(data.errorMessage ?? "Video processing failed");
    }

    if (data.status === "ready" && data.playbackId && data.assetId) {
      return {
        assetId: data.assetId,
        playbackId: data.playbackId,
        status: "ready",
        duration: data.duration,
        width: data.width,
        height: data.height,
      };
    }

    if (
      waitUntil === "asset_created" &&
      data.assetId &&
      (data.status === "asset_created" || data.status === "ready")
    ) {
      return {
        assetId: data.assetId,
        playbackId: data.playbackId ?? "",
        status:
          data.status === "ready" && data.playbackId ? "ready" : "preparing",
        duration: data.duration,
        width: data.width,
        height: data.height,
      };
    }
  }

  throw new Error(
    waitUntil === "asset_created"
      ? "Video upload timed out before Mux created an asset. Please try again."
      : "Video processing timed out — it may still be processing. Try refreshing.",
  );
}
